import type { FlexProps } from "@qws-org/ui-kit";
import {
  Flex,
  Image,
  Markdown,
  Title,
  useUiKitComponents,
} from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages, Social_Networks } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type SocialNetworksProps = {
  data: Social_Networks;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isActionInactive: (action: Action) => boolean;
} & FlexProps;

export const SocialNetworks: FC<SocialNetworksProps> = ({
  data,
  getUrl,
  onCallAction,
  isActionInactive,
  ...rest
}) => {
  const { Link } = useUiKitComponents();
  const { getDirectusImage } = useDirectus();

  return (
    <Flex
      flexDirection="column"
      p={{ py: 56, px: 15 }}
      {...rest}
      md={{ p: { py: 80 }, ...rest.md }}
      data-testid="social-networks"
    >
      <Title
        as="h2"
        size="small"
        lineHeight="title.mobile.medium"
        fontSize="title.mobile.medium"
        fontWeight="mobile.semibold"
        m={{ mb: 40 }}
        md={{
          size: "large",
          m: { mb: 64 },
          fontWeight: "desktop.semibold",
          fontSize: "title.desktop.medium",
          lineHeight: "title.desktop.medium",
        }}
        horizontalAlign="center"
        data-testid="social-networks-title"
      >
        {data.title}
      </Title>

      <Flex
        justify="center"
        gap={32}
        flexWrap="wrap"
        m={{ mb: 40 }}
        md={{ gap: 80, m: { mb: 40 } }}
      >
        {data.socials
          ?.filter((item) => {
            if (item?.social_network_items_id?.action) {
              return !isActionInactive(item.social_network_items_id.action);
            }

            return true;
          })
          .map((item, idx) => {
            const social = item?.social_network_items_id;
            const controlAction = social?.action;
            const action = social?.action?.action?.[0];
            let href;

            if (isExternalLink(action?.item)) {
              href = action.item.href ?? undefined;
            }
            if (isInternalLink(action?.item)) {
              href = action.item.page ? getUrl(action.item.page) : undefined;
            }

            const handlePressButton = (): void => {
              if (controlAction) {
                onCallAction(controlAction);
              }
            };

            return (
              social?.icon?.id && (
                <Link
                  onClick={handlePressButton}
                  href={href}
                  key={social?.id}
                  data-testid={`social-network-item-${idx}`}
                >
                  <Image
                    width={64}
                    md={{ width: "auto" }}
                    className="hover:drop-shadow-[0px_4px_30px_var(--colors-text-secondary-default)] drop-shadow-[0px_1px_1px_var(--colors-surface-neutral-subtle)] transition-filter duration-200 "
                    value={getDirectusImage(social.icon.id, {
                      format: "svg",
                      width: 64,
                    })}
                    mdValue={getDirectusImage(social.icon.id, {
                      format: "svg",
                      width: 120,
                    })}
                  />
                </Link>
              )
            );
          })}
      </Flex>
      {data.description && (
        <Markdown data-testid="social-network-description">
          {data.description}
        </Markdown>
      )}
    </Flex>
  );
};
